/*
 * Dad Bites — regional review ordering.
 *
 * Pure, dependency-free logic for deciding which of a product's REAL reviews
 * to show first, based on the language the visitor most likely reads.
 *
 * It only ever reorders reviews that already exist. It never invents one,
 * never changes a rating, and never shows a review belonging to a different
 * product. The visitor's region is used to pick an order and is never
 * displayed.
 *
 * Exposed as window.DadBitesRegionalReviews so it can be unit-tested in the
 * browser console independently of any review source.
 */
(function (global) {
  'use strict';

  // Fallback chains. First entry is the visitor's own language.
  var LANGUAGE_PRIORITY = {
    en: ['en', 'hi', 'mr', 'te', 'ta', 'kn', 'ml', 'as'],
    hi: ['hi', 'en', 'mr', 'te', 'ta', 'kn', 'ml', 'as'],
    mr: ['mr', 'hi', 'en', 'te', 'ta', 'kn', 'ml', 'as'],
    ta: ['ta', 'en', 'hi', 'te', 'ml', 'kn', 'mr', 'as'],
    te: ['te', 'en', 'hi', 'ta', 'kn', 'mr', 'ml', 'as'],
    kn: ['kn', 'en', 'hi', 'te', 'ta', 'ml', 'mr', 'as'],
    ml: ['ml', 'en', 'hi', 'ta', 'te', 'kn', 'mr', 'as'],
    as: ['as', 'hi', 'en', 'bn', 'te', 'ta', 'mr', 'kn'],
    bn: ['bn', 'hi', 'en', 'as', 'te', 'ta', 'mr', 'kn']
  };

  // Used only when the browser gives no usable language signal.
  var REGION_LANGUAGE = {
    'IN-DL': 'hi', 'IN-UP': 'hi', 'IN-BR': 'hi', 'IN-RJ': 'hi',
    'IN-MP': 'hi', 'IN-HR': 'hi', 'IN-UK': 'hi', 'IN-JH': 'hi', 'IN-CG': 'hi',
    'IN-MH': 'mr', 'IN-GA': 'mr',
    'IN-TN': 'ta', 'IN-PY': 'ta',
    'IN-TG': 'te', 'IN-AP': 'te',
    'IN-KA': 'kn',
    'IN-KL': 'ml',
    'IN-AS': 'as', 'IN-ML': 'as', 'IN-NL': 'as', 'IN-MN': 'as',
    'IN-TR': 'as', 'IN-AR': 'as', 'IN-MZ': 'as',
    'IN-WB': 'bn'
  };

  var DEFAULT_LANGUAGE = 'en';

  function normalise(tag) {
    if (!tag) return null;
    var base = String(tag).toLowerCase().split('-')[0];
    return LANGUAGE_PRIORITY[base] ? base : null;
  }

  /*
   * Resolve the visitor's language, in the order the spec asks for:
   *   1. Shopify storefront locale
   *   2. Browser languages
   *   3. Region, only as a hint and never on its own
   *   4. English
   * IP-derived region is deliberately last, because VPNs, travel and
   * corporate networks routinely get it wrong.
   */
  function resolveLanguage(context) {
    var ctx = context || {};

    var fromShopify = normalise(ctx.shopifyLocale);
    if (fromShopify) return fromShopify;

    var browserTags = ctx.browserLanguages;
    if (!browserTags && global.navigator) {
      browserTags = global.navigator.languages || [global.navigator.language];
    }
    if (browserTags) {
      for (var i = 0; i < browserTags.length; i++) {
        var hit = normalise(browserTags[i]);
        if (hit) return hit;
      }
    }

    if (ctx.region && REGION_LANGUAGE[ctx.region]) return REGION_LANGUAGE[ctx.region];

    return DEFAULT_LANGUAGE;
  }

  function priorityFor(language) {
    return LANGUAGE_PRIORITY[language] || LANGUAGE_PRIORITY[DEFAULT_LANGUAGE];
  }

  // Deterministic shuffle so the same visitor sees a stable order on a page,
  // while different visitors see different arrangements.
  function shuffle(list, seed) {
    var out = list.slice();
    var s = typeof seed === 'number' ? seed : Math.floor(Math.random() * 100000);
    for (var i = out.length - 1; i > 0; i--) {
      s = (s * 1103515245 + 12345) & 0x7fffffff;
      var j = s % (i + 1);
      var tmp = out[i]; out[i] = out[j]; out[j] = tmp;
    }
    return out;
  }

  /*
   * Order a product's reviews for one visitor.
   *
   * reviews  array of { language, ... }; anything else on the object is kept
   * options  { language, limit, localShare, seed }
   *
   * localShare caps how much of the list may be the visitor's own language,
   * so the section still reads as a nationwide customer base rather than a
   * page that has been obviously localised. Defaults to 0.65.
   */
  function orderReviews(reviews, options) {
    if (!Array.isArray(reviews) || reviews.length === 0) return [];

    var opts = options || {};
    var language = opts.language || resolveLanguage(opts);
    var limit = typeof opts.limit === 'number' ? opts.limit : reviews.length;
    var share = typeof opts.localShare === 'number' ? opts.localShare : 0.65;
    var seed = opts.seed;

    var chain = priorityFor(language);

    // Bucket by language, preserving anything with an unknown tag.
    var buckets = {};
    var unknown = [];
    reviews.forEach(function (review) {
      var tag = normalise(review && review.language);
      if (!tag) { unknown.push(review); return; }
      (buckets[tag] = buckets[tag] || []).push(review);
    });

    Object.keys(buckets).forEach(function (tag) {
      buckets[tag] = shuffle(buckets[tag], seed);
    });

    var localMax = Math.max(1, Math.floor(limit * share));
    var primary = buckets[language] || [];
    var ordered = primary.slice(0, localMax);

    // Fill the rest down the fallback chain, skipping the primary language.
    for (var i = 0; i < chain.length && ordered.length < limit; i++) {
      var tag = chain[i];
      if (tag === language) continue;
      var pool = buckets[tag] || [];
      for (var j = 0; j < pool.length && ordered.length < limit; j++) {
        ordered.push(pool[j]);
      }
    }

    // Any leftover primary-language reviews, then untagged ones.
    for (var k = localMax; k < primary.length && ordered.length < limit; k++) {
      ordered.push(primary[k]);
    }
    var extras = shuffle(unknown, seed);
    for (var m = 0; m < extras.length && ordered.length < limit; m++) {
      ordered.push(extras[m]);
    }

    return ordered;
  }

  global.DadBitesRegionalReviews = {
    resolveLanguage: resolveLanguage,
    priorityFor: priorityFor,
    orderReviews: orderReviews,
    LANGUAGE_PRIORITY: LANGUAGE_PRIORITY,
    REGION_LANGUAGE: REGION_LANGUAGE
  };
})(window);
